import React from 'react'
import { Grid, MenuItem, Select, InputLabel, FormControl, Checkbox, FormControlLabel, IconButton } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { useState, useEffect, useContext } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';
//Components
import CMLabel from '../../components/atoms/CMLabel';
import CMButton from '../../components/atoms/CMButton';
import CMTextField from '../../components/atoms/CMTextField';
//Context
import { UserContext } from '../../context/UserContext';
//Services
import * as UsuarioService from '../../services/UsuarioService';

const formInicial = {
    usuario: '',
    contrasena: '',
    id_rol: 2,
    nombres: '',
    apellidos: '',
    correo: '',
    id_area: '',
    solicitante: false,
    aprobador: false
};

const roles = [
    { id_rol: 2, nombre_rol: 'Solicitante' },
    { id_rol: 3, nombre_rol: 'Aprobador' }
];

const GestionUsuarios = () => {
    const [rowsdata, setRowsdata] = useState([]);
    const [areas, setAreas] = useState([]);
    const [form, setForm] = useState(formInicial);
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const { user } = useContext(UserContext);
    const history = useHistory();

    useEffect(() => {
        UsuarioService.getUsuarios(setRowsdata, user.token);
        UsuarioService.getAreas(setAreas, user.token);
    }, []);

    const updateUsuarios = () => {
        UsuarioService.getUsuarios(setRowsdata, user.token);
    }

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm({ ...form, [field]: value });
        setErrors({ ...errors, [field]: null });
    };

    const validar = () => {
        const nuevosErrores = {};
        if (!form.usuario.trim()) nuevosErrores.usuario = 'Ingrese el usuario';
        if (!editingId && !form.contrasena.trim()) nuevosErrores.contrasena = 'Ingrese la contraseña';
        if (!form.nombres.trim()) nuevosErrores.nombres = 'Ingrese los nombres';
        if (!form.apellidos.trim()) nuevosErrores.apellidos = 'Ingrese los apellidos';
        if (!form.id_area) nuevosErrores.id_area = 'Seleccione un área';
        if (!form.correo.trim()) {
            nuevosErrores.correo = 'Ingrese el correo';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo)) {
            nuevosErrores.correo = 'Correo inválido';
        }
        setErrors(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const limpiarForm = () => {
        setForm(formInicial);
        setEditingId(null);
        setErrors({});
    };

    const handleGuardar = async () => {
        if (!validar()) return;
        setLoading(true);
        try {
            const datos = {
                ...form,
                id_usuario: editingId,
                solicitante: form.solicitante,
                aprobador: form.aprobador
            };
            let result;
            if (editingId) {
                result = await UsuarioService.updateUsuario(datos, user.token);
            } else {
                result = await UsuarioService.createUsuario(datos, user.token);
            }
            Swal.fire('Correcto', result.mensaje, 'success');
            limpiarForm();
            updateUsuarios();
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.response?.data || 'Ocurrió un error al guardar el usuario';
            Swal.fire('Error', mensaje, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditar = (row) => {
        setEditingId(row.id_usuario);
        setForm({
            usuario: row.usuario,
            contrasena: '',
            id_rol: row.id_rol,
            nombres: row.nombres || '',
            apellidos: row.apellidos || '',
            correo: row.correo || '',
            id_area: row.id_area || '',
            solicitante: !!row.solicitante,
            aprobador: !!row.aprobador
        });
        setErrors({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleEliminar = (row) => {
        Swal.fire({
            title: 'Eliminar Usuario',
            text: `¿Está seguro de que desea eliminar al usuario ${row.usuario}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await UsuarioService.deleteUsuario(row.id_usuario, user.token);
                    Swal.fire('Correcto', response.mensaje, 'success');
                    if (editingId === row.id_usuario) limpiarForm();
                    updateUsuarios();
                } catch (error) {
                    const mensaje = error.response?.data?.mensaje || 'Hubo un error al eliminar el usuario';
                    Swal.fire('Error', mensaje, 'error');
                }
            }
        });
    };

    const columns = [
        { field: 'id_usuario', headerName: 'ID', width: 80 },
        { field: 'usuario', headerName: 'Usuario', width: 140 },
        { field: 'nombre_rol', headerName: 'Rol', width: 120 },
        { field: 'nom_completo', headerName: 'Nombre Completo', width: 240 },
        { field: 'correo', headerName: 'Correo', width: 240 },
        { field: 'nombre_area', headerName: 'Área', width: 180 },
        {
            field: 'solicitante', headerName: 'Solicitante', width: 110,
            valueGetter: (params) => params.row.solicitante ? 'Sí' : 'No'
        },
        {
            field: 'aprobador', headerName: 'Aprobador', width: 110,
            valueGetter: (params) => params.row.aprobador ? 'Sí' : 'No'
        },
        {
            field: 'acciones', headerName: 'Acciones', width: 140, sortable: false, filterable: false,
            renderCell: (params) => (
                <>
                    <IconButton size="small" color="primary" title="Editar" onClick={() => handleEditar(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="secondary" title="Permisos" onClick={() => history.push('/permisosusuarios', { id_usuario: params.row.id_usuario })}>
                        <ManageAccountsIcon />
                    </IconButton>
                    <IconButton size="small" color="error" title="Eliminar" onClick={() => handleEliminar(params.row)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Grid width={'100%'} m="auto">
            <Grid container justifyContent="flex-start" alignItems="center" spacing={2} sx={{ py: 1 }}>
                <Grid container item xs={12} sm={12}>
                    <Grid item sm={6}>
                        <CMLabel sx={{ margin: 2 }} fontSize='24px' color="black"><b>Gestión de Usuarios</b></CMLabel>
                    </Grid>
                </Grid>
                <Grid container item xs={12} sm={12} justifyContent="center">
                    <Grid item sm={12}>
                        <hr></hr>
                        <Grid container item xs={12} sm={12} alignItems="center" spacing={2} sx={{ py: 1 }}>
                            <Grid item xs={12} sm={2}>
                                <CMTextField
                                    label="Usuario"
                                    value={form.usuario}
                                    onChange={handleChange('usuario')}
                                    error={errors.usuario}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <CMTextField
                                    label={editingId ? "Contraseña (vacío = sin cambio)" : "Contraseña"}
                                    type="password"
                                    value={form.contrasena}
                                    onChange={handleChange('contrasena')}
                                    error={errors.contrasena}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl variant="filled" sx={{ width: '100%' }} error={!!errors.id_rol}>
                                    <InputLabel>Rol</InputLabel>
                                    <Select
                                        value={form.id_rol}
                                        onChange={handleChange('id_rol')}
                                    >
                                        {roles.map(rol => (
                                            <MenuItem key={rol.id_rol} value={rol.id_rol}>{rol.nombre_rol}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl variant="filled" sx={{ width: '100%' }} error={!!errors.id_area}>
                                    <InputLabel>Área</InputLabel>
                                    <Select
                                        value={form.id_area}
                                        onChange={handleChange('id_area')}
                                    >
                                        {areas.map(area => (
                                            <MenuItem key={area.id_area} value={area.id_area}>{area.nombre_area}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {errors.id_area && <CMLabel fontSize='12px' color='#d32f2f'>{errors.id_area}</CMLabel>}
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <CMTextField
                                    label="Nombres"
                                    value={form.nombres}
                                    onChange={handleChange('nombres')}
                                    error={errors.nombres}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <CMTextField
                                    label="Apellidos"
                                    value={form.apellidos}
                                    onChange={handleChange('apellidos')}
                                    error={errors.apellidos}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <CMTextField
                                    label="Correo"
                                    type="email"
                                    value={form.correo}
                                    onChange={handleChange('correo')}
                                    error={errors.correo}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControlLabel
                                    control={<Checkbox checked={form.solicitante} onChange={handleChange('solicitante')} />}
                                    label="Solicitante"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={form.aprobador} onChange={handleChange('aprobador')} />}
                                    label="Aprobador"
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <CMButton
                                    label={editingId ? "Actualizar" : "Crear"}
                                    variant="contained"
                                    disabled={loading}
                                    onClick={handleGuardar}
                                />
                            </Grid>
                            {editingId && (
                                <Grid item xs={12} sm={2}>
                                    <CMButton
                                        label="Cancelar"
                                        variant="outlined"
                                        onClick={limpiarForm}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container item xs={12} sm={12}>
                    <Grid style={{ height: 480, width: '100%', overflowX: 'auto' }} sx={{ py: 0 }}>
                        <DataGrid
                            rows={rowsdata}
                            columns={columns}
                            pageSize={15}
                            rowsPerPageOptions={[15]}
                            sx={{
                                height: "100%",
                                "& .MuiDataGrid-window": {
                                    overflowX: 'scroll',
                                },
                            }}
                            pagination
                            rowHeight={50}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default GestionUsuarios
